import React, {useState, useContext} from 'react'
import {createPortal} from 'react-dom'
import {Modal} from 'react-bootstrap'
import { ProductsContext } from '../../context'
// import {Editor, EditorState} from 'draft-js';
// import 'draft-js/dist/Draft.css';
import config from '../../../config.json'
import DPicker from '../../components/DatePicker'
import moment from 'moment'

const modalsRoot = document.getElementById('root-modals') || document.body
const CHANNEL_ASSURANCESKI = 'assuranceski'
const CHANNEL_MYSPORTCOVER = 'mysportcover'

const normalizeChannels = (channels) => {
  return Array.isArray(channels) && channels.length > 0 ? channels : [CHANNEL_ASSURANCESKI]
}

const LocalProductDetailModal = ({show, handleClose, product, setProduct, onSubmit, onDelete}) => {

  const { specificProducts, genericProducts, resorts, durations, domains, pricingRules } = useContext(ProductsContext)
  const [selectedLanguage, setSelectedLanguage] = useState('fr')

  const genericProductForLocalProduct = (localProduct) => {
    const specificProduct = specificProducts.find(p => p.id === localProduct?.specific_product)
    if(!specificProduct) return null
    return genericProducts.find(p => p.id === specificProduct.generic_product) || null
  }

  const channelsForLocalProduct = (localProduct) => {
    return normalizeChannels(genericProductForLocalProduct(localProduct)?.channels)
  }

  const updateData = (fieldsToUpdate) => {
    const updatedData = {...product, ...fieldsToUpdate}
    const channels = channelsForLocalProduct(updatedData)
    const hasMySportCoverChannel = channels.includes(CHANNEL_MYSPORTCOVER)
    delete updatedData.channels
    delete updatedData.mysportcover
    if(hasMySportCoverChannel) {
      updatedData.all_resorts = true
      updatedData.resort = ''
      updatedData.referral_id = ''
      updatedData.sale_start_date = ''
      updatedData.sale_end_date = ''
    } else if(fieldsToUpdate.all_resorts === false) {
      updatedData.referral_id = ''
    } else if(fieldsToUpdate.all_resorts === true) {
      updatedData.resort = ''
    }
    setProduct(updatedData)
  }

  const handleSubmit = async () => {
    const data = {...product}
    const pricingRulePrices = {...(data.pricing_rule_prices || {})}
    for(const [ruleId, price] of Object.entries(pricingRulePrices)) {
      const rule = pricingRules.find(item => item.id === ruleId)
      if(rule && !pricingRuleApplicableToProduct(rule, data)) {
        alert(`${rule.name} cannot be applied because this product's duration does not require dates`)
        return
      }
      const numericPrice = Number(price)
      if(!Number.isFinite(numericPrice) || numericPrice < 0) {
        alert(`A valid public price is required for ${rule?.name || ruleId}`)
        return
      }
      pricingRulePrices[ruleId] = numericPrice
    }
    data.pricing_rule_prices = pricingRulePrices
    const channels = channelsForLocalProduct(data)
    const hasMySportCoverChannel = channels.includes(CHANNEL_MYSPORTCOVER)
    delete data.channels
    delete data.mysportcover
    if(hasMySportCoverChannel) {
      data.all_resorts = true
      data.resort = ''
      data.referral_id = ''
      data.sale_start_date = ''
      data.sale_end_date = ''
    }
    onSubmit(data)
  }

  const detailsForProduct = () => {
    if(!product?.specific_product) return ""
    const specificProduct = specificProducts.find(p => p.id === product.specific_product)
    if(!specificProduct) return ""
    const genericProduct = genericProducts.find(p => p.id === specificProduct.generic_product)
    if(!genericProduct) return ""
    const duration = durations.find(p => p.id === genericProduct.duration)
    const domain = domains.find(p => p.id === genericProduct.domain)

    return `Generic: ${genericProduct.name?.[selectedLanguage]} - ${domain?.title?.[selectedLanguage] || '(no domain)'} - ${duration?.title?.[selectedLanguage] || '(no duration)'}`
  }

  const handleImportDates = () => {
    const resort = resorts.find(r => r.id === product.resort)
    if(!resort) {
      alert('No resort selected')
      return
    }
    const updates = {}
    if(resort.sale_start_date) updates.sale_start_date = resort.sale_start_date
    if(resort.sale_end_date) updates.sale_end_date = resort.sale_end_date
    if(Object.keys(updates).length === 0) {
      alert('No dates found for this resort')
      return
    }
    updateData(updates)
  }

  const pricingRuleConditionLabel = (rule) => {
    const conditions = []
    const minDates = rule.conditions?.min_dates || rule.conditions?.min_days
    if(rule.conditions?.min_people) conditions.push(`${rule.conditions.min_people}+ people`)
    if(minDates) conditions.push(`${minDates}+ days`)
    return conditions.join(' and ') || 'No conditions'
  }

  const productRequiresDates = (localProduct) => {
    const genericProduct = genericProductForLocalProduct(localProduct)
    const duration = durations.find(item => item.id === genericProduct?.duration)
    return duration?.dates_required === true
  }

  const pricingRuleApplicableToProduct = (rule, localProduct) => {
    const hasMinimumDates = Boolean(rule.conditions?.min_dates || rule.conditions?.min_days)
    return !hasMinimumDates || productRequiresDates(localProduct)
  }

  const setPricingRuleAssigned = (ruleId, assigned) => {
    const prices = {...(product?.pricing_rule_prices || {})}
    if(assigned) {
      prices[ruleId] = Number(product?.price) || 0
    } else {
      delete prices[ruleId]
    }
    updateData({pricing_rule_prices: prices})
  }

  const setPricingRulePrice = (ruleId, value) => {
    updateData({
      pricing_rule_prices: {
        ...(product?.pricing_rule_prices || {}),
        [ruleId]: value === '' ? '' : Number.parseFloat(value),
      },
    })
  }

  const availablePricingRules = pricingRules.filter(rule => (
    rule.active !== false || product?.pricing_rule_prices?.[rule.id] !== undefined
  ))

  const productChannels = channelsForLocalProduct(product)
  const isMySportCover = productChannels.includes(CHANNEL_MYSPORTCOVER)
  const isAllResorts = Boolean(product?.all_resorts || isMySportCover)

  return createPortal(
    <Modal
      id='kt_modal_create_app'
      tabIndex={-1}
      aria-hidden='true'
      dialogClassName='modal-dialog modal-dialog-centered mw-900px'
      show={show}
      onHide={handleClose}
      backdrop={true}
    >
      <div className='modal-header'>
        <div className='as-form-row align-items-center justify-content-between'>
          <h2>{ product?.id ? 'Update Local Product' : 'Create Local Product' }</h2>
          <div>
            <label className='mb-2'>
              <span>Language</span>
            </label>
            <div className='nav-group nav-group-outline nav-group-slim'>
              <label>
                <input
                  type='radio'
                  className='btn-check'
                  name='type'
                  value='fr'
                  checked={selectedLanguage === 'fr'}
                  onChange={() => setSelectedLanguage('fr')}
                />
                <span className='btn btn-sm btn-color-muted btn-active btn-active-primary'>
                  FR
                </span>
              </label>

              <label>
                <input type='radio' className='btn-check' name='type' value='en' checked={selectedLanguage === 'en'} onChange={() => setSelectedLanguage('en')} />
                <span className='btn btn-sm btn-color-muted btn-active btn-active-primary px-4'>
                  EN
                </span>
              </label>

              <label>
                <input type='radio' className='btn-check' name='type' value='es' checked={selectedLanguage === 'es'} onChange={() => setSelectedLanguage('es')} />
                <span className='btn btn-sm btn-color-muted btn-active btn-active-primary px-4'>
                  ES
                </span>
              </label>

              <label>
                <input type='radio' className='btn-check' name='type' value='it' checked={selectedLanguage === 'it'} onChange={() => setSelectedLanguage('it')} />
                <span className='btn btn-sm btn-color-muted btn-active btn-active-primary px-4'>
                  IT
                </span>
              </label>
            </div>
          </div>
        </div>
      </div>

      <div className='modal-body'>
        <div className='flex-row-fluid px-lg-5'>
            <div className='fv-row mb-4'>
              <label className='mb-2'>
                <span>Created: <b>{moment(product?.meta?.created || 0).format('DD/MM/YYYY HH:mm')}</b></span><br />
                <span>Updated: <b>{moment(product?.meta?.updated || 0).format('DD/MM/YYYY HH:mm')}</b></span>
                {config.environment === 'development' && <><br /><span>ID: <b>{product?.id}</b></span></>}
              </label>
            </div>

            <div className='fv-row mb-4'>
              <label className='mb-2'>
                <span className='required'>Specific product</span>
              </label>
              <select className='form-select form-select-solid form-select-lg' name="specific_product" id="specific_product" value={product?.specific_product || ""} onChange={e => updateData({
                  specific_product: e.target.value
                }
              )}>
                <option disabled value="">{"Select Specific Product"}</option>
                { specificProducts.map((p, pi) =>
                  <option key={`specific_product_option_${pi}`} value={p.id}>{p.name?.fr}</option>
                )}
              </select>
            </div>
            <div className='mb-6'>
              <span className='text-muted'>{ detailsForProduct() }</span>
            </div>

            <div className='fv-row mb-6'>
              <label className='mb-2'>
                <span className='required'>Resort</span>
              </label>
              <div className='mt-2 mb-4 fw-semibold form-check form-switch form-switch-sm form-check-custom form-check-solid'>
                <input
                  className='form-check-input'
                  type='checkbox'
                  value=''
                  name='all-resorts'
                  checked={isAllResorts}
                  disabled={isMySportCover}
                  onChange={e => updateData({ all_resorts: e.target.checked })}
                />
                <label className='form-check-label'>{ "All resorts" }</label>
              </div>
              { !isAllResorts &&
                <select className='form-select form-select-solid form-select-lg' name="resort" id="resort" value={product?.resort || ""} onChange={e => updateData({
                    resort: e.target.value
                  }
                )}>
                  <option disabled value="">{"Select resort"}</option>
                  { resorts.map((r, ri) =>
                    <option key={`resort_option_${ri}`} value={r.id}>{r.name.fr}</option>
                  )}
                </select>
              }
              {product?.resort_name && <label className="mt-2"><span className='text-muted'>{ `Col h: ${product.resort_name}` }</span></label>}
            </div>

            { isAllResorts && !isMySportCover &&
              <div className='fv-row mb-6'>
                <label className='mb-2'>
                  <span>Referral ID</span>
                </label>

            {/* <div className='mb-6'> */}
            {/* </div> */}
                <input
                  type='text'
                  className='form-control form-control-lg form-control-solid'
                  name='product-referral-id'
                  placeholder=''
                  value={product?.referral_id || ''}
                  onChange={(e) =>
                    updateData({
                      referral_id: e.target.value
                    })
                  }
                />
                <div className='mt-2'>
                  <span className='text-muted'>For direct purchase URL</span>
                </div>
              </div>
            }

            { !isMySportCover &&
              <div className='d-flex flex-row gap-3 align-items-end mb-6'>
                <div className='fv-row'>
                  <label className='d-flex align-items-center fs-5 fw-semibold mb-2'>
                    <span>Sale start date</span>
                  </label>
                  <DPicker selected={product?.sale_start_date} onChange={(date) => {
                    updateData({
                      sale_start_date: date,
                    })
                  }} />
                </div>
                <div className='fv-row'>
                  <label className='d-flex align-items-center fs-5 fw-semibold mb-2'>
                    <span>Sale end date</span>
                  </label>
                  <DPicker  selected={product?.sale_end_date} onChange={(date) => {
                    updateData({
                      sale_end_date: date,
                    })
                  }} />
                </div>
                <div>
                  <button className='btn btn-secondary btn-sm' onClick={handleImportDates}>Import from resort (legacy)</button>
                </div>
              </div>
            }

            <div className='fv-row mb-6'>
              <label className='mb-2'>
                <span>Public Price</span>
              </label>
              <input
                type='number'
                className='form-control form-control-lg form-control-solid'
                name='product-price'
                placeholder=''
                value={product?.price || 0}
                onChange={(e) =>
                  updateData({
                    price: parseFloat(e.target.value)
                  })
                }
              />
            </div>

            <div className='fv-row mb-8'>
              <label className='mb-3'>
                <span className='fs-5 fw-semibold'>Pricing rules</span>
              </label>
              {availablePricingRules.length === 0 ? (
                <div className='text-muted'>No pricing rules have been created.</div>
              ) : (
                <div className='d-flex flex-column gap-3'>
                  {availablePricingRules.map(rule => {
                    const assigned = product?.pricing_rule_prices?.[rule.id] !== undefined
                    const applicable = pricingRuleApplicableToProduct(rule, product)
                    return (
                      <div key={rule.id} className='d-flex flex-wrap align-items-center gap-3 border-bottom pb-3'>
                        <div className='form-check form-check-custom form-check-solid min-w-200px'>
                          <input
                            className='form-check-input'
                            type='checkbox'
                            id={`pricing-rule-${rule.id}`}
                            checked={assigned}
                            disabled={!applicable && !assigned}
                            onChange={(event) => setPricingRuleAssigned(rule.id, event.target.checked)}
                          />
                          <label className='form-check-label ms-2' htmlFor={`pricing-rule-${rule.id}`}>
                            <span className='d-block fw-semibold'>{rule.name}</span>
                            <span className='d-block text-muted fs-8'>
                              {pricingRuleConditionLabel(rule)}{rule.active === false ? ' (disabled)' : ''}
                            </span>
                            {!applicable && (
                              <span className='d-block text-danger fs-8'>Requires a duration with dates</span>
                            )}
                          </label>
                        </div>
                        <div className='d-flex align-items-center gap-2 ms-auto'>
                          <input
                            type='number'
                            min='0'
                            step='0.01'
                            className='form-control form-control-solid w-150px'
                            aria-label={`Public price for ${rule.name}`}
                            disabled={!assigned || !applicable}
                            value={assigned ? product.pricing_rule_prices[rule.id] : ''}
                            onChange={(event) => setPricingRulePrice(rule.id, event.target.value)}
                          />
                          <span>EUR</span>
                        </div>
                      </div>
                    )
                  })}
                </div>
              )}
              <div className='text-muted mt-3'>The base public price is used when no assigned rule matches.</div>
            </div>

            <div className='fv-row mb-6'>
              <label className='mb-2'>
                <span>Price without tax</span>
              </label>
              <input
                type='number'
                className='form-control form-control-lg form-control-solid'
                name='product-price-ht'
                placeholder=''
                value={product?.price_ht || 0}
                onChange={(e) =>
                  updateData({
                    price_ht: parseFloat(e.target.value)
                  })
                }
              />
            </div>

            <div className='fv-row mb-6'>
              <label className='mb-2'>
                <span>Insurance tax</span>
              </label>
              <input
                type='number'
                className='form-control form-control-lg form-control-solid'
                name='product-insurance-tax'
                placeholder=''
                value={product?.insurance_tax || 0}
                onChange={(e) =>
                  updateData({
                    insurance_tax: parseFloat(e.target.value)
                  })
                }
              />
            </div>

            <div className='fv-row mb-6'>
              <label className='mb-2'>
                <span>Operator share</span>
              </label>
              <input
                type='number'
                className='form-control form-control-lg form-control-solid'
                name='product-operator-share'
                placeholder=''
                value={product?.share_operator || 0}
                onChange={(e) =>
                  updateData({
                    share_operator: parseFloat(e.target.value)
                  })
                }
              />
            </div>

            <div className='fv-row mb-6'>
              <label className='mb-2'>
                <span>Clubs share</span>
              </label>
              <input
                type='number'
                className='form-control form-control-lg form-control-solid'
                name='product-clubs-share'
                placeholder=''
                value={product?.share_club || 0}
                onChange={(e) =>
                  updateData({
                    share_club: parseFloat(e.target.value)
                  })
                }
              />
            </div>

            <div className='fv-row mb-6'>
              <label className='mb-2'>
                <span>Others share</span>
              </label>
              <input
                type='number'
                className='form-control form-control-lg form-control-solid'
                name='product-others-share'
                placeholder=''
                value={product?.share_other || 0}
                onChange={(e) =>
                  updateData({
                    share_other: parseFloat(e.target.value)
                  })
                }
              />
            </div>

            <div className='fv-row mb-6'>
              <label className='mb-2'>
                <span>Publisher share</span>
              </label>
              <input
                type='number'
                className='form-control form-control-lg form-control-solid'
                name='product-publisher-share'
                placeholder=''
                value={product?.share_webapp || 0}
                onChange={(e) =>
                  updateData({
                    share_webapp: parseFloat(e.target.value)
                  })
                }
              />
            </div>

            {/* TODO determine if this is redundant when networks have resorts associated */}
            {/* <div className='fv-row mb-4'>
              <label className='mb-2'>
                <span>Network</span>
              </label>
              <select className='form-select form-select-solid form-select-lg' name="reseller" id="reseller" value={product?.reseller || ""} onChange={e => updateData({
                  reseller: e.target.value
                }
              )}>
                <option disabled value="">{"Select Network"}</option>
                { resellers.map((r, ri) =>
                  <option key={`reseller_option_${ri}`} value={r.id}>{r.company_name}</option>
                )}
              </select>
              {product?.company_name && <label className="mt-2"><span className='text-muted'>{ `Col c: ${product.company_name}` }</span></label>}
            </div> */}

            {/*begin::Actions */}
            <div className='d-flex flex-row-fluid justify-content-between align-items-center gap-2 gap-lg-3 mt-10'>
              <div className='me-2'>
              { product?.id &&
                <button
                  type='button'
                  className='btn btn-lg btn-danger'
                  data-kt-stepper-action='next'
                  onClick={onDelete}
                >
                  { 'Delete' }
                </button> }
              </div>
              <div className='d-flex gap-3'>
                <button
                  type='button'
                  className='btn btn-lg'
                  data-kt-stepper-action='next'
                  onClick={handleClose}
                >
                  { 'Close' }
                </button>
                <button
                  type='button'
                  className='btn btn-lg btn-primary'
                  data-kt-stepper-action='next'
                  onClick={handleSubmit}
                >
                  { product?.id ? 'Update' : 'Create' }
                </button>
              </div>
            </div>
            {/*end::Actions */}
        </div>
        {/*end::Content */}
      </div>
    </Modal>,
    modalsRoot
  )
}

export default LocalProductDetailModal
